import { Typography } from '@euler/app/components/typography';
import { useUserProfileManager } from '@euler/app/flows/settings/functions';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { Button } from '@euler/components/Button';
import { useModalWrapperSize } from '@euler/components/ModalWrapper';
import { FormGroup, FormInput } from '@euler/components/form';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBackButtonHandler } from '@euler/functions/useBackButtonHandler';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { PredefinedVerifyCodeScene } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { delayed, sha256, showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useRef, useState } from 'react';
import { ScrollView, TextInput, TouchableOpacity } from 'react-native';
import Toast from 'react-native-root-toast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import { BackButton } from '../components/BackButton';
import { CustomPageHeader } from '../components/CustomPageHeader';

const kCodeLength = 6;

export const ChangePasswordScreen = wrapNavigatorScreen(
  () => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    useModalWrapperSize({ width: rpx(375), height: rpx(500) });
    const onGoBack = useBackButtonHandler();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();
    const loading = useAppLoading();
    const [isLoading, setIsLoading] = useState(false);
    const nonce = useRef(nanoid()).current;
    const ticket = useRef<string>();
    const newPasswordInputRef = useRef<TextInput>();
    const { smsService, authService } = useServiceFactory();
    const { profile } = useUserProfileManager();
    const [oldPassword, setOldPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const isValid =
      oldPassword.trim().length > 0 && newPassword.trim().length > 0;

    const sendCode = useCallback(
      async (
        callback?: (
          error: Error | null | undefined,
          result?: {
            ticket: string;
            length: number;
            ttl: number;
          },
        ) => void,
      ) => {
        const mobile = profile.user.mobile;

        if (!mobile) {
          void showAlert('您还没有设置手机号!');
          return;
        }

        if (!ticket.current) {
          ticket.current = await smsService.requestVerifyCodeTicket(nonce);
        }

        loading.show();
        setIsLoading(true);

        try {
          const ttl = await smsService.sendVerifyCode(
            ticket.current,
            mobile,
            PredefinedVerifyCodeScene.Login,
            kCodeLength,
          );
          setIsLoading(false);
          loading.hide();
          Toast.show('验证码已发送', {
            duration: 300,
          });
          if (callback) {
            callback(null, {
              ticket: ticket.current,
              length: kCodeLength,
              ttl,
            });
          } else {
            navigation.push('_verifyCode', {
              mobile,
              codeInfo: {
                ticket: ticket.current,
                length: kCodeLength,
                ttl,
              },
              onSendCode: sendCode,
              onDone: async params => {
                navigation.push('_newPassword', {
                  onDone: async password => {
                    const hashedPassword = await sha256(password.trim());

                    try {
                      loading.show();
                      await authService.changePassword({
                        type: 'phone',
                        mobile: params.mobile,
                        ticket: params.ticket,
                        verifyCode: params.verifyCode,
                        newPassword: hashedPassword,
                      });
                      navigation.pop(3);
                      delayed(() => {
                        Toast.show('密码已重置', {
                          duration: 1500,
                          position: Toast.positions.BOTTOM,
                        });
                      }, 500);
                    } catch (e) {
                      void showAlert('出错了', (e as Error).message);
                    } finally {
                      loading.hide();
                    }
                  },
                });

                // todo
              },
            });
          }
        } catch (e) {
          setIsLoading(false);
          loading.hide();
          Toast.show('发送失败: ' + (e as Error).message, {
            duration: 3000,
            position: Toast.positions.BOTTOM,
          });
          if (callback) {
            callback(e as Error);
          }
        }
      },
      [
        profile.user.mobile,
        loading,
        smsService,
        nonce,
        navigation,
        authService,
      ],
    );

    const onResetPassword = usePersistFn(async () => {
      if (isLoading) return;
      await sendCode();
    });

    const onNextPress = useCallback(() => {
      newPasswordInputRef.current?.focus();
    }, []);

    const onConfirm = usePersistFn(async () => {
      try {
        if (!isValid) return;
        loading.show();
        await authService.changePassword({
          type: 'old-password',
          oldPassword: await sha256(oldPassword.trim()),
          newPassword: await sha256(newPassword.trim()),
        });
        navigation.goBack();
        delayed(() => {
          Toast.show('密码修改成功', {
            duration: 1500,
            position: Toast.positions.BOTTOM,
          });
        }, 500);
      } catch (e) {
        void showAlert('出错了', (e as Error).message);
      } finally {
        loading.hide();
      }
    });

    return (
      <>
        <ScrollView
          contentContainerStyle={{
            paddingTop: insets.top + rpx(80),
            paddingLeft: rpx(20),
            paddingRight: rpx(20),
            paddingBottom: rpx(20),
          }}
        >
          <CustomPageHeader title={'修改登录密码'} />

          <FormGroup
            css={`
              margin-top: ${rpx(32)}px;
            `}
          >
            <FormInput
              placeholder="请输入旧密码"
              value={oldPassword}
              onChange={setOldPassword}
              isPassword
              keyboardType="ascii-capable"
              returnKeyType="next"
              onSubmitEditing={onNextPress}
              autoFocus
              css={`
                font-size: ${rpx(16)}px;
                font-family: ${FontFamily.NotoSans.Medium};
                letter-spacing: 0.5px;
              `}
            />
            <HStack
              justifyContent="flex-start"
              css={`
                margin-top: ${rpx(8)}px;
                margin-left: ${rpx(15)}px;
              `}
            >
              <Typography.Label light color="#555" size={rpx(14)}>
                忘记密码?{' '}
              </Typography.Label>
              <TouchableOpacity onPress={onResetPassword}>
                <Typography.Label light color={theme.link} size={rpx(14)}>
                  通过手机验证码重置密码
                </Typography.Label>
              </TouchableOpacity>
            </HStack>
          </FormGroup>

          <FormGroup>
            <FormInput
              inputRef={newPasswordInputRef}
              placeholder="请输入新密码"
              value={newPassword}
              onChange={setNewPassword}
              isPassword
              keyboardType="ascii-capable"
              returnKeyType="done"
              onSubmitEditing={onConfirm}
              css={`
                font-size: ${rpx(16)}px;
                font-family: ${FontFamily.NotoSans.Medium};
                letter-spacing: 0.5px;
              `}
            />
          </FormGroup>

          <Button
            title="确认修改"
            disabled={!isValid}
            onPress={onConfirm}
            textStyle={{ color: isValid ? '#fff' : '#ddd', fontSize: rpx(16) }}
            css={`
              background-color: ${theme.colors.primary};
            `}
          />
        </ScrollView>
        <BackButton
          onPress={onGoBack}
          color={'#333'}
          css={`
            position: absolute;
            left: ${rpx(20)}px;
            top: ${insets.top + rpx(15)}px;
          `}
        />
      </>
    );
  },
  { headerShown: false },
);
